import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IS_DEV = process.env.NODE_ENV === 'development';

const PAID_STATUSES = ['paid'];
const PENDING_STATUSES = ['pending', 'open'];
const FAILED_STATUSES = ['failed', 'canceled', 'expired'];

const STATUS_VISUALS = {
  paid: { bg: 'bg-green-500/20', title: 'Betaling geslaagd!', subtitle: 'Je bestelling wordt bereid' },
  pending: { bg: 'bg-yellow-500/20', title: 'Betaling in behandeling', subtitle: 'Even geduld, je betaling wordt verwerkt...' },
  open: { bg: 'bg-yellow-500/20', title: 'Betaling in behandeling', subtitle: 'Even geduld, je betaling wordt verwerkt...' },
  failed: { bg: 'bg-red-500/20', title: 'Betaling mislukt', subtitle: 'De betaling kon niet worden verwerkt' },
  canceled: { bg: 'bg-red-500/20', title: 'Betaling geannuleerd', subtitle: 'Je hebt de betaling geannuleerd' },
  expired: { bg: 'bg-red-500/20', title: 'Betaling verlopen', subtitle: 'De betalingstermijn is verlopen' },
};
const FALLBACK_VISUAL = { bg: 'bg-red-500/20', title: 'Onbekende status', subtitle: 'Neem contact op met personeel' };

const visualsFor = (status) => STATUS_VISUALS[status] || FALLBACK_VISUAL;

const StatusIcon = ({ status }) => {
  if (PAID_STATUSES.includes(status)) return <CheckCircle2 className="w-20 h-20 text-green-500" />;
  if (PENDING_STATUSES.includes(status)) return <Clock className="w-20 h-20 text-yellow-500" />;
  return <XCircle className="w-20 h-20 text-red-500" />;
};

const PaidActions = ({ orderId, onContinue }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
    <p className="text-sm text-muted-foreground mb-6">
      Bestelnummer: {orderId?.substring(0, 8).toUpperCase()}
    </p>
    <Button
      onClick={onContinue}
      size="lg"
      data-testid="go-to-confirmation-btn"
      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6"
    >
      Bekijk bevestiging
    </Button>
  </motion.div>
);

const PendingIndicator = () => (
  <div className="space-y-4">
    <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
    <p className="text-sm text-muted-foreground">Status wordt automatisch bijgewerkt...</p>
  </div>
);

const FailedActions = ({ onRetry, onNewOrder }) => (
  <div className="space-y-4">
    <Button
      onClick={onRetry}
      size="lg"
      data-testid="retry-btn"
      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6"
    >
      Opnieuw betalen
    </Button>
    <Button
      onClick={onNewOrder}
      variant="outline"
      size="lg"
      className="w-full text-lg py-6"
    >
      Nieuwe bestelling
    </Button>
  </div>
);

const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Card className="p-12 bg-card border-2 border-border text-center max-w-lg">
      <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
      <h2 className="text-2xl font-bold text-foreground mb-4">Betaling controleren...</h2>
      <p className="text-muted-foreground">Even geduld</p>
    </Card>
  </div>
);

export const PaymentResult = ({ order, setOrder, resetOrder }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  const orderId = searchParams.get('orderId') || order.orderId;

  const checkStatus = useCallback(async () => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/payments/status/${orderId}`);
      if (!response.ok) throw new Error('Status ophalen mislukt');
      const data = await response.json();

      setPaymentStatus(data.payment_status);
      setOrder(prev => ({ ...prev, orderId, timestamp: new Date().toISOString() }));

      if (PENDING_STATUSES.includes(data.payment_status) && pollCount < 10) {
        setTimeout(() => setPollCount(c => c + 1), 2000);
      }
    } catch (err) {
      if (IS_DEV) console.error('Status check error:', err);
      setPaymentStatus('unknown');
    } finally {
      setLoading(false);
    }
  }, [orderId, pollCount, setOrder]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  if (loading) return <LoadingScreen />;

  const isPaid = PAID_STATUSES.includes(paymentStatus);
  const isPending = PENDING_STATUSES.includes(paymentStatus);
  const showFailedActions = FAILED_STATUSES.includes(paymentStatus) || paymentStatus === 'unknown';
  const visuals = visualsFor(paymentStatus);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <Card className="p-12 bg-card border-2 border-border text-center">
          <div className={`w-28 h-28 mx-auto mb-6 rounded-full flex items-center justify-center ${visuals.bg}`}>
            <StatusIcon status={paymentStatus} />
          </div>

          <h2 className="text-3xl font-bold text-foreground mb-4">{visuals.title}</h2>
          <p className="text-lg text-muted-foreground mb-6">{visuals.subtitle}</p>

          {isPaid && <PaidActions orderId={orderId} onContinue={() => navigate('/confirmation')} />}
          {isPending && <PendingIndicator />}
          {showFailedActions && (
            <FailedActions
              onRetry={() => navigate('/payment')}
              onNewOrder={() => { resetOrder(); navigate('/'); }}
            />
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentResult;
